import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

import {
	AddressInterface,
	OccupancyRestrictionsInterface,
} from '@valk-nx/core/lib/interfaces/hotels.interface';
import { PriceInterface } from '@valk-nx/core/lib/interfaces/price.interface';

export interface EventData {
	guid: string;
	slug: string;
	cta: {
		labelKey?: EventCtaLabelKeys;
		url: string;
	};
	name: string;
	description: string;
	shortDescription: string;
	bulletContent: string;
	meta: MetaData;
	schedule: EventSchedule;
	address: HotelAddress;
	price: PriceInterface;
	faqs: Faq[];
	images: PhotoInterface[];
}

export interface Faq {
	question: string;
	answer: string;
}

export type HotelAddress = AddressInterface;

export interface EventSchedule {
	startDate: string;
	startTime: string;
	endDate: string;
	endTime: string;
	isRecurring: boolean;
	dates: string[] | null;
}

export type EventCtaLabelKeys =
	| 'visitwebsite'
	| 'booknow'
	| 'requestnow'
	| 'signup'
	| 'bookdirectly'
	| 'buytickets'
	| 'nolongeravailable';

export interface Icon {
	iconName: string;
	title: string;
}

export interface HotelData {
	guid: string;
	currency: string;
	facilities: string[];
	goodToKnows: string[];
	images: PhotoInterface[];
	rating: Rating;
}

export interface RoomFacilities {
	bathroom: string[];
	generic: string[];
	misc: string[];
	sleepingComfort: string[];
}

export interface Rating {
	numberOfRatings: number;
	score: number;
	stars: number;
}

export interface MetaData {
	title: string;
	description: string;
}

export interface PhotoInterface {
	src: string;
	alt?: string;
}

export interface RoomData {
	accommodationType: 'apartments' | 'holiday-homes' | 'rooms';
	bookingToolRedirectUrl?: string;
	category: string;
	checkInTime: string;
	checkOutTime: string;
	description: string;
	facilities: RoomFacilities;
	guid: string;
	hotel: HotelData;
	images: PhotoInterface[];
	isBookable: boolean;
	meta: MetaData;
	occupancyRestrictions: OccupancyRestrictionsInterface;
	price: PriceInterface;
	slug: string;
	summary: string;
	surfaceArea: number;
	title: string;
}

export interface EventIconListOptions {
	dateFormat?: string;
	timeInputFormat?: string;
	timeOutputFormat?: string;
	translateFn?: (key: string) => string;
}

export function buildIconListRoom(room: RoomData): Icon[] {
	const iconList: Icon[] = [];
	const { facilities } = room;
	if (room.surfaceArea) {
		iconList.push({
			iconName: 'icon-surface-area',
			title: (room.surfaceArea / 10000).toString() + 'm²',
		});
	}
	if (facilities.sleepingComfort && facilities.sleepingComfort.length > 0) {
		iconList.push({
			iconName: 'icon-bed-double',
			title: facilities.sleepingComfort[0],
		});
	}
	if (facilities.bathroom && facilities.bathroom.length > 0) {
		iconList.push({
			iconName: 'icon-shower-bath',
			title: facilities.bathroom[0],
		});
	}
	return iconList;
}

export function buildEventIconList(
	event: EventData,
	options: EventIconListOptions = {},
): Icon[] {
	const {
		dateFormat = 'DD MMM YYYY',
		timeInputFormat = 'HH:mm:ss',
		timeOutputFormat = 'HH:mm',
		translateFn = (key: string) => key,
	} = options;

	const iconList: Icon[] = [];
	const { schedule } = event;

	if (schedule.startDate) {
		const prettyStartDate = dayjs(schedule.startDate).format(dateFormat);
		if (schedule.startDate === schedule.endDate) {
			iconList.push({
				iconName: 'icon-calendar',
				title: prettyStartDate,
			});
		} else {
			const prettyEndDate = dayjs(schedule.endDate).format(dateFormat);
			iconList.push({
				iconName: 'icon-calendar',
				title: `${prettyStartDate} - ${prettyEndDate}`,
			});
		}
		if (schedule.isRecurring) {
			iconList.push({
				iconName: 'icon-spinner',
				title: translateFn(
					'storyblok.pages.event-overview.recurring-event',
				),
			});
		}
		if (schedule.startTime) {
			const prettyStartTime = dayjs(
				schedule.startTime,
				timeInputFormat,
			).format(timeOutputFormat);
			if (schedule.endTime) {
				const prettyEndTime = dayjs(
					schedule.endTime,
					timeInputFormat,
				).format(timeOutputFormat);
				iconList.push({
					iconName: 'icon-clock',
					title: `${prettyStartTime} - ${prettyEndTime}`,
				});
			} else {
				iconList.push({
					iconName: 'icon-clock',
					title: prettyStartTime,
				});
			}
		}
	}

	return iconList;
}
